/**
 * Selecting a theme selects its default dataset — S3's clause 5, as one function.
 *
 * The client's table says what each theme opens on; `apps/meta/themes.py` resolves it
 * against the catalogue and `themes.ts` reads it. This is the third step: turning that row
 * into the two store writes that actually move the map, in **one** place, because three
 * controls reach it — the rail chip, the cold-boot default view, and (through the rail) a
 * reader coming back to a theme they left.
 *
 * ## What a theme selection clears
 *
 * A theme is a mode, the way the two pane tabs were before S3 removed them: what the map
 * shows is what the open theme is about. So picking a theme with a layer default drops the
 * mapped variable, and picking one with a variable default drops the raster.
 *
 * **All** has no default of its own (the client's table says "all datasets, excluding
 * indicators"), and since the client's 23 September note it does not leave the previous
 * theme's data behind either: arriving there widens the list and *empties* the map, because
 * there is nothing for it to be about. The hierarchy boundaries stay, drawn in one colour.
 *
 * **Digital Agriculture clears and draws nothing.** `in_preparation` is the client's own
 * state for a theme with no dataset, and leaving the previous theme's raster underneath an
 * "in preparation" panel would be a map contradicting its own legend.
 */
import type { Indicator, Layer, MetaConfig } from '../../lib/api';
import {
  effectiveLevels,
  selectIndicatorMapped,
  selectLayersMapped,
  useAppStore,
} from '../../lib/state';
import { toast } from '../../lib/toast';
import { useHierarchyStore } from '../map/hierarchy';
import { isRasterKind } from '../map/layerKinds';
import { firstPaneClearance } from './paneSwitch';
import {
  ALL_THEME,
  domFilterOf,
  INDICATORS_THEME,
  themeDefaultDataset,
  themeEntry,
  themeOf,
} from './themes';

/** The catalogues this needs; both may still be loading, and then nothing is applied. */
export interface ThemeCatalogues {
  config: MetaConfig | undefined;
  layers: Layer[] | undefined;
  indicators: Indicator[] | undefined;
}

/**
 * Apply `theme`'s default dataset to the map. Returns what it selected, so a caller that
 * only wants to act once (the boot default) can tell an applied theme from a skipped one.
 *
 * Skipped — `null` — when the catalogues have not landed, or when the server's row names a
 * dataset this catalogue does not carry. "We do not know yet" is never acted on: applying a half-loaded table would clear a reader's map on a slow boot.
 */
export function applyThemeDefault(
  theme: string,
  { config, layers, indicators }: ThemeCatalogues,
): { kind: 'layer' | 'variable' | 'none'; id: string | null } | null {
  const entry = themeEntry(config, theme);
  if (!entry || !layers) return null;

  const store = useAppStore.getState();
  const rasterIds = layers.filter((l) => isRasterKind(l.kind)).map((l) => l.id);
  const clearRaster = () => store.setActiveRaster(null, rasterIds);

  if (entry.in_preparation) {
    clearRaster();
    store.setIndicator(null);
    return { kind: 'none', id: null };
  }
  /**
   * **All clears the map** — client change, 23 September 2026.
   *
   * > *"when a user clicks on 'All' on the rail, if there was any data there it should
   * > disappear and we should have a single symbol colour for the operational hierarchy."*
   *
   * All used to be the one theme that left the map alone, on the reading that a chip with no
   * default of its own has nothing to say about what is drawn. The client reads it the other
   * way round: All is *no theme*, so it is *no data* — the boundaries in one colour and
   * nothing else. So this drops all three kinds of mapping — the active raster, the mapped
   * variable and every ticked vector layer — rather than skipping.
   *
   * It returns `{kind:'none'}` and not `null`: `null` means "nothing was applied, the caller
   * may still act", which is what the boot default view asks. Clearing the map is an action.
   */
  if (theme === ALL_THEME) {
    // All starts at the country level (client, 2026-09-23): the four countries, one colour,
    // and the categories on hover; regions come back when the reader ticks them or zooms.
    const rows = useHierarchyStore.getState();
    rows.setVisible('adm0', true);
    rows.setVisible('adm1', false);
    clearRaster();
    store.setIndicator(null);
    // A copy: `toggleLayer` writes a new `layersOn` on each call, and iterating the live set
    // while the store replaces it is the classic way to leave one layer behind.
    for (const id of [...store.layersOn]) store.toggleLayer(id, false);
    return { kind: 'none', id: null };
  }

  const wanted = themeDefaultDataset(entry);
  if (!wanted) return null;

  if (wanted.kind === 'layer') {
    const layer = layers.find((l) => l.id === wanted.id);
    // A row the catalogue does not carry, or one this role may not read: the panel prints
    // the theme's note and the empty state rather than asking TiTiler for a 500.
    if (!layer || layer.access === 'none' || layer.unavailable) return null;
    store.setIndicator(null);
    if (isRasterKind(layer.kind)) store.setActiveRaster(layer.id, rasterIds);
    else store.toggleLayer(layer.id, true);
    return { kind: 'layer', id: layer.id };
  }

  if (!indicators) return null;
  const variable = indicators.find((i) => i.id === wanted.id);
  if (!variable || !variable.active) return null;
  clearRaster();
  store.setIndicator(variable.id, variable.has_watershed_values, effectiveLevels(variable));
  return { kind: 'variable', id: variable.id };
}

/**
 * **Select `target` as the reader's theme** — the rail's own store path, as one function.
 *
 * `DomainRail`'s chip used to own this: the §F5.11/R4 E2 clearance across the Indicators
 * boundary, then `setDomFilter`, then `applyThemeDefault`. §F9's "Preview on map" (client
 * item 9) has to land a card on *its dataset's* theme, and a second implementation of
 * "select a theme" is a second set of rules for what a theme selection clears — so the
 * writes live here and the rail calls them.
 *
 * What is **not** here is the rail's "click the selected chip to clear back to All": that is
 * a property of a toggle control, not of selecting a theme, and a preview that cleared the
 * theme because the reader happened to already be on it would be the §F9 bug in a new place.
 * The rail keeps that decision and passes the target it wants.
 *
 * `translate` is the caller's `t` — this module is not a component and must not reach for a
 * hook, and the two clearance strings are the caller's to localise (§F12, en and fr).
 */
export function selectTheme(
  target: string,
  catalogues: ThemeCatalogues,
  translate: (key: string) => string,
): void {
  const store = useAppStore.getState();
  const current = themeOf(store.domFilter);
  const entering = target === INDICATORS_THEME;
  const leaving = current === INDICATORS_THEME && !entering;
  if (entering || leaving) {
    const cleared = entering
      ? selectLayersMapped(store) && 'layers'
      : selectIndicatorMapped(store) && 'indicator';
    store.switchSidebarTab(entering ? 'inds' : 'layers');
    if (cleared && firstPaneClearance(cleared)) toast(translate(`sidebar.${cleared}Cleared`));
  }
  store.setDomFilter(domFilterOf(target));
  applyThemeDefault(target, catalogues);
}
